import { z } from 'zod';
import { createSchema } from 'zod-openapi';
import { COUNTRY_CODES } from './countries.catalog';
import {
	AVAILABILITY,
	DRIVING_LICENCE,
	GCC_EXPERIENCE,
	LANGUAGE_LEVELS,
	MANAGEMENT_EXPERIENCE,
	SALARY_EXPECTATIONS,
	VISA_OPTIONS,
	YEARS_OF_EXPERIENCE,
} from './merit.catalogs';

// ============================================
// SHARED
// ============================================

/**
 * Nacionalidad como código ISO 3166-1 alfa-2.
 *
 * Se valida contra `COUNTRY_CODES` y no con un `/^[A-Z]{2}$/`: la cascada de la
 * interfaz garantiza que todo código tiene continente, y `continentOf()` es
 * quien precarga el desplegable al editar. Un código bien formado pero fuera
 * del catálogo (`SU`, `YU`, un territorio que no ofrecemos) dejaría la ficha
 * con un país que ningún continente puede seleccionar, así que se rechaza en
 * la puerta en vez de aparecer como un desplegable vacío tres pantallas
 * después.
 */
export const CountryCodeSchema = z
	.string()
	.refine((code) => (COUNTRY_CODES as readonly string[]).includes(code), { message: 'UNKNOWN_COUNTRY_CODE' })
	.meta({ title: 'Country code (ISO 3166-1 alpha-2)', example: 'PH' });

/** País de un empleo concreto. Lo escribe el equipo cuando el CV lo dice. */
export const SetExperienceCountrySchema = z.object({
	countryCode: CountryCodeSchema,
});
export type SetExperienceCountryDTO = z.infer<typeof SetExperienceCountrySchema>;

/**
 * How Kairos reads one compound title for this person. `canonical: null`
 * clears the row. Experience rows are stored and do not grant Encaje alto.
 */
export const SavePositionNormalizationSchema = z.object({
	source: z.enum(['target', 'experience']),
	originalTitle: z.string().trim().min(1).max(200),
	experienceId: z.number().int().min(0).default(0),
	canonical: z.string().trim().min(1).max(200).nullable(),
	grade: z.string().trim().min(1).max(80).nullable().optional(),
	specialty: z.string().trim().min(1).max(80).nullable().optional(),
	scope: z.string().trim().min(1).max(80).nullable().optional(),
	secondaryCanonical: z.string().trim().min(1).max(200).nullable().optional(),
});
export type SavePositionNormalizationDTO = z.infer<typeof SavePositionNormalizationSchema>;

/**
 * One spoken language with its CEFR self-reported level. Replaces the old
 * flat `string[]`: the scoring engine needs the level to grade English (0–12)
 * and the best second language (0–8). See `candidate-scoring.default.ts`.
 */
export const LanguageSkillSchema = z.object({
	// Free string, not an enum: the value comes from the `languages` catalog, which
	// the admin can extend from /catalogs (see the note above CreateCandidateSchema).
	language: z.string().min(1).meta({ title: 'Language', example: 'English' }),
	level: z.enum(LANGUAGE_LEVELS).meta({ title: 'CEFR level', example: 'C1' }),
});

// ============================================
// REQUEST — public intake (candidate-form.md)
// ============================================

/**
 * Public candidate application. All fields are mandatory per the form spec.
 * Files (CV / picture / video) travel as multipart and are validated in the
 * storage layer, not here. `*Other` free-text applies when the matching select
 * is "Other"; `source` marks where the lead came from (form, referral, event…).
 *
 * Fields backed by a DB catalog (`positions`, `languages`, `cities`,
 * `luxury_levels`) are plain strings, not enums: the admin adds and renames those
 * values in /catalogs, so a hard-coded whitelist here would reject anything added
 * after this file was written. The stored value is still the catalog label.
 */
export const CreateCandidateSchema = z.object({
	fullName: z.string().min(1).meta({
		title: 'Full name',
		example: 'John Doe',
	}),
	phone: z.string().min(1).meta({
		title: 'Phone number',
		description: 'Calls & WhatsApp',
		example: '+971500000000',
	}),
	email: z.string().email().meta({
		title: 'Email',
		example: 'john@example.com',
	}),
	languages: z
		.array(LanguageSkillSchema)
		.min(1)
		.meta({
			title: 'Languages',
			description: 'Languages the candidate speaks, each with a CEFR level',
			example: [
				{ language: 'English', level: 'C1' },
				{ language: 'Arabic', level: 'B2' },
			],
		}),
	availability: z.enum(AVAILABILITY).meta({ title: 'Availability', example: 'Immediately' }),
	// Texto libre histórico. Ya no se teclea —lo deriva la cascada de países—,
	// pero se conserva: es lo que declaró la persona, y sin el original no hay
	// forma de revisar una correspondencia dudosa (ver `CandidateLead.countryCode`).
	nationality: z.string().min(1).meta({ title: 'Nationality', example: 'Spanish' }),
	/**
	 * País de nacionalidad. Opcional a propósito: la cascada lo exige en el
	 * formulario, pero la API la usan también el importador de GHL y las fichas
	 * antiguas, que solo traen el texto libre. Exigirlo aquí rompería el alta de
	 * todo lo que no pase por la pantalla.
	 */
	countryCode: CountryCodeSchema.nullable().optional(),
	lastPosition: z.string().min(1).meta({ title: 'Last position', example: 'Concierge' }),
	lastPositionOther: z.string().optional().nullable().meta({
		title: 'Last position (Other)',
		description: 'Free text when last position = Other',
	}),
	// «Otro» retirado de los puestos objetivo (decisión d, 02/09): se elige del
	// listado sí o sí. El servidor lo rechaza aunque el formulario lo cuele.
	targetPosition: z
		.string()
		.min(1)
		.refine((value) => value !== 'Other', 'TARGET_POSITION_OTHER_RETIRED')
		.meta({ title: 'Target position', example: 'Guest Relations Officer' }),
	targetPositionOther: z.string().optional().nullable().meta({
		title: 'Target position (Other)',
		description: 'Free text when target position = Other',
	}),
	yearsOfExperience: z.enum(YEARS_OF_EXPERIENCE).meta({ title: 'Years of experience', example: '3–5 years' }),
	drivingLicence: z.enum(DRIVING_LICENCE).meta({ title: 'Driving licence', example: 'UAE License' }),
	salaryExpectation: z.enum(SALARY_EXPECTATIONS).meta({
		title: 'Salary expectations (AED)',
		example: '6,000 – 10,000',
	}),
	visaStatus: z.enum(VISA_OPTIONS).meta({ title: 'Current visa status', example: 'Own visa' }),
	luxuryLevel: z.string().min(1).meta({
		title: 'Luxury hospitality level',
		example: '4–5 Star Hospitality Level',
	}),
	managementExperience: z.enum(MANAGEMENT_EXPERIENCE).meta({
		title: 'Management experience',
		example: 'Supervisor Level',
	}),
	// Location (Scoring only). `currentLocation`/`locationTargeting` hold GCC_LOCATIONS
	// values (or 'Other'); when 'Other' is chosen, the free country/city go in the
	// matching *Other* fields. Targeting is multi-select.
	currentLocation: z.string().min(1).meta({ title: 'Current location', example: 'Dubai, United Arab Emirates' }),
	currentLocationOtherCountry: z.string().optional().nullable().meta({ title: 'Current location country (Other)' }),
	currentLocationOtherCity: z.string().optional().nullable().meta({ title: 'Current location city (Other)' }),
	locationTargeting: z
		.array(z.string().min(1))
		.min(1)
		.meta({ title: 'Location targeting', example: ['Dubai, United Arab Emirates', 'Doha, Qatar'] }),
	locationTargetingOtherCountry: z.string().optional().nullable().meta({ title: 'Targeting country (Other)' }),
	locationTargetingOtherCity: z.string().optional().nullable().meta({ title: 'Targeting city (Other)' }),
	gccExperience: z.enum(GCC_EXPERIENCE).meta({ title: 'GCC experience', example: 'UAE Experience' }),
	/**
	 * Altura en cm. **Obligatoria en el formulario, opcional en la API**, y la
	 * asimetría es deliberada: la misma que tiene `countryCode`. Esta ruta la usan
	 * también el importador de GHL y las fichas antiguas, que no traen el dato;
	 * exigirla aquí rompería toda alta que no pase por la pantalla, sin ganar nada
	 * — el campo no decide nada.
	 *
	 * **Nunca filtra ni descarta** (decisión escrita de la clienta, y ya escrita
	 * también en `CandidatePersonal.heightCm`): la apariencia física es criterio
	 * prohibido en el motor de matching y este campo no es la excepción. Se pide
	 * porque algunas propiedades la usan como referencia, y se enseña; nada más.
	 */
	heightCm: z.number().int().min(100).max(250).meta({
		title: 'Height (cm)',
		description: 'Required since 02/09 (C-P1-10); informative only — never filters or discards',
		example: 175,
	}),
	/**
	 * Habilidades declaradas por la persona (25/08 · fase 5 §6.3).
	 *
	 * Texto libre y no catálogo: `systems` y `specializations` ya cubren lo que sí
	 * tiene lista cerrada (herramientas y áreas), y lo que faltaba era justo lo
	 * que no cabe en una — «servicio de vinos», «protocolo VIP», «apertura de
	 * hotel». Un catálogo aquí obligaría a la clienta a mantenerlo antes de que
	 * nadie sepa qué escribe la gente.
	 */
	skills: z
		.array(z.string().trim().min(1).max(60))
		.max(20)
		.optional()
		.meta({ title: 'Skills', example: ['Wine service', 'VIP protocol'] }),
	linkedinUrl: z.string().url().meta({
		title: 'LinkedIn profile URL',
		example: 'https://linkedin.com/in/johndoe',
	}),
	// PDPL consent (back in the public form since 10/09): required to submit from
	// the public route, absent when the team creates a lead from the backoffice.
	consent: z.boolean().optional().meta({
		title: 'Privacy consent',
		description: 'PDPL checkbox of the public intake form',
	}),
	source: z.string().optional().nullable().meta({
		title: 'Source',
		description: 'Where the lead came from',
		example: 'form',
	}),
});

/**
 * Partial update of a candidate's form fields (admin edit, perm
 * `lead-candidate:write`). Every field optional; re-scoring runs after the edit.
 */
export const UpdateCandidateSchema = CreateCandidateSchema.partial();

// ============================================
// RESPONSE
// ============================================

export const CandidateResponseSchema = z.object({
	id: z.number().int().meta({ title: 'Candidate ID', example: 1 }),
	fullName: z.string(),
	phone: z.string(),
	email: z.string().email(),
	languages: z.array(LanguageSkillSchema),
	availability: z.string(),
	nationality: z.string(),
	countryCode: z.string().nullable(),
	lastPosition: z.string(),
	lastPositionOther: z.string().nullable(),
	targetPosition: z.string(),
	targetPositionOther: z.string().nullable(),
	/** Los tres puestos objetivo, el principal primero (07/10). */
	targetPositions: z.array(z.string()).optional(),
	yearsOfExperience: z.string(),
	drivingLicence: z.string(),
	salaryExpectation: z.string(),
	visaStatus: z.string(),
	luxuryLevel: z.string(),
	managementExperience: z.string(),
	currentLocation: z.string().nullable(),
	currentLocationOtherCountry: z.string().nullable(),
	currentLocationOtherCity: z.string().nullable(),
	locationTargeting: z.array(z.string()),
	locationTargetingOtherCountry: z.string().nullable(),
	locationTargetingOtherCity: z.string().nullable(),
	gccExperience: z.string(),
	/** Informativa; nunca filtra ni descarta. */
	heightCm: z.number().int().nullable(),
	skills: z.array(z.string()),
	linkedinUrl: z.string(),
	source: z.string().nullable(),
	status: z.string().meta({
		title: 'Status',
		description: 'processing | approved | completed | rejected',
		example: 'processing',
	}),
	createdAt: z.string().datetime(),
	updatedAt: z.string().datetime(),
});

export const CandidateListResponseSchema = z.object({
	data: z.array(CandidateResponseSchema),
	total: z.number(),
	page: z.number(),
	limit: z.number(),
});

/**
 * Public read-only view of a candidate application (Fase 3 completion page).
 * Served by `GET /candidates/complete/:token`. Deliberately excludes the score
 * and any internal validation data — the candidate never sees the number.
 */
export const PublicCandidateDocumentSchema = z.object({
	documentType: z.string(),
	originalName: z.string(),
	mimeType: z.string().nullable(),
	publicUrl: z.string(),
});

export const PublicCandidateApplicationSchema = CandidateResponseSchema.extend({
	documents: z.array(PublicCandidateDocumentSchema),
});

// ============================================
// TYPE EXPORTS
// ============================================

export type LanguageSkillDTO = z.infer<typeof LanguageSkillSchema>;
export type CreateCandidateDTO = z.infer<typeof CreateCandidateSchema>;
export type UpdateCandidateDTO = z.infer<typeof UpdateCandidateSchema>;
export type CandidateResponseDTO = z.infer<typeof CandidateResponseSchema>;
export type CandidateListResponseDTO = z.infer<typeof CandidateListResponseSchema>;
export type PublicCandidateDocumentDTO = z.infer<typeof PublicCandidateDocumentSchema>;
export type PublicCandidateApplicationDTO = z.infer<typeof PublicCandidateApplicationSchema>;

// ============================================
// OPENAPI SCHEMAS
// ============================================

export const { schema: CreateCandidateRequestOpenAPI } = createSchema(CreateCandidateSchema);
export const { schema: UpdateCandidateRequestOpenAPI } = createSchema(UpdateCandidateSchema);
export const { schema: CreateCandidateResponseOpenAPI } = createSchema(CandidateResponseSchema);
export const { schema: GetCandidateResponseOpenAPI } = createSchema(CandidateResponseSchema);
export const { schema: GetAllCandidatesResponseOpenAPI } = createSchema(CandidateListResponseSchema);
